import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Grid,
  Group,
  Paper,
  PasswordInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { createUser, deleteUser, listShares, listUsers, revokeShare, shareFolder, updateUser, type UserOut } from '../api/gen';
import { AUTH_MODE, MIN_PASSWORD } from '../lib/auth';

// The admin's page (concept section 8): create users, make admins, and share
// folders with them. One page of each list; paging and filters are there in
// the API (listquery) for when a shelf outgrows a hundred.
export function UsersPage() {
  const users = useQuery({ queryKey: ['users'], queryFn: () => unwrap(listUsers({ query: { limit: 100 } })) });
  return (
    <Stack maw={1100} mx="auto" gap="xl">
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Users users={users.data?.data ?? []} error={users.error as Error | null} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <NewUser />
        </Grid.Col>
      </Grid>
      <Shares users={users.data?.data ?? []} />
    </Stack>
  );
}

function failed(title: string) {
  return (e: unknown) => notifications.show({ color: 'red', title, message: (e as Error).message });
}

function Users({ users, error }: { users: UserOut[]; error: Error | null }) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['users'] });
  const promote = useMutation({
    mutationFn: (u: UserOut) => unwrap(updateUser({ path: { id: u.id }, body: { isAdmin: !u.isAdmin } })),
    onSuccess: refresh,
    onError: failed('Not changed'),
  });
  const remove = useMutation({
    mutationFn: (u: UserOut) => unwrap(deleteUser({ path: { id: u.id } })),
    onSuccess: () => {
      refresh();
      void qc.invalidateQueries({ queryKey: ['shares'] });
    },
    onError: failed('Not removed'),
  });
  return (
    <Stack gap="sm">
      <Title order={3}>Users</Title>
      {error ? <Alert color="red">{error.message}</Alert> : null}
      <Paper withBorder>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th w={90}>Admin</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td>
                  <Text size="sm">{u.email}</Text>
                </Table.Td>
                <Table.Td>
                  <Switch checked={u.isAdmin} onChange={() => promote.mutate(u)} aria-label={`Admin: ${u.email}`} />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Remove, with every share">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={`Remove ${u.email}`}
                      onClick={() => {
                        if (window.confirm(`Remove ${u.email}? Their shares go with them; the bucket is untouched.`)) remove.mutate(u);
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

function NewUser() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [admin, setAdmin] = useState(false);
  const header = AUTH_MODE === 'header';
  const create = useMutation({
    mutationFn: () => unwrap(createUser({ body: { email: email.trim(), password: header ? undefined : password, isAdmin: admin } })),
    onSuccess: (u) => {
      notifications.show({ title: 'Access given', message: header ? u.email : `${u.email} can sign in with the password you set.` });
      setEmail('');
      setPassword('');
      setAdmin(false);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  return (
    <Paper withBorder p="md">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Stack gap="sm">
          <Title order={5}>Give an address access</Title>
          <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          {header ? null : (
            <PasswordInput
              label="First password"
              description={`At least ${MIN_PASSWORD} characters. Pass it on yourself; nothing is mailed.`}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          )}
          <Checkbox label="Admin: sees the whole shelf, manages users" checked={admin} onChange={(e) => setAdmin(e.currentTarget.checked)} />
          {create.error ? <Alert color="red">{(create.error as Error).message}</Alert> : null}
          <Button type="submit" loading={create.isPending} disabled={!email.trim() || (!header && password.length < MIN_PASSWORD)}>
            Add user
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

function Shares({ users }: { users: UserOut[] }) {
  const qc = useQueryClient();
  const shares = useQuery({ queryKey: ['shares'], queryFn: () => unwrap(listShares({ query: { limit: 100 } })) });
  const [userId, setUserId] = useState<string | null>(null);
  const [folder, setFolder] = useState('');
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const grant = useMutation({
    mutationFn: () => unwrap(shareFolder({ body: { userId: userId!, folder: folder.trim(), permission } })),
    onSuccess: () => {
      setFolder('');
      void qc.invalidateQueries({ queryKey: ['shares'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(revokeShare({ path: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shares'] }),
    onError: failed('Not revoked'),
  });
  const people = users.filter((u) => !u.isAdmin).map((u) => ({ value: u.id, label: u.email }));

  return (
    <Stack gap="sm">
      <div>
        <Title order={3}>Shares</Title>
        <Text size="sm" c="dimmed">
          A share covers everything under its folder, including what is uploaded later. Where two shares overlap, the
          narrower one decides.
        </Text>
      </div>
      <Paper withBorder p="md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            grant.mutate();
          }}
        >
          <Group align="flex-end" gap="sm">
            <Select label="User" placeholder="Pick a user" data={people} value={userId} onChange={setUserId} searchable w={260} />
            <TextInput
              label="Folder"
              placeholder="invoicer/v2.3/"
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
              value={folder}
              onChange={(e) => setFolder(e.currentTarget.value)}
              flex={1}
              miw={200}
            />
            <SegmentedControl
              value={permission}
              onChange={(v) => setPermission(v as 'read' | 'write')}
              data={[
                { value: 'read', label: 'Read' },
                { value: 'write', label: 'Write' },
              ]}
            />
            <Button type="submit" loading={grant.isPending} disabled={!userId || !folder.trim()}>
              Share
            </Button>
          </Group>
          {grant.error ? (
            <Alert color="red" mt="sm">
              {(grant.error as Error).message}
            </Alert>
          ) : null}
        </form>
      </Paper>
      <Paper withBorder>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Folder</Table.Th>
              <Table.Th w={100}>Permission</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(shares.data?.data ?? []).map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td>
                  <Text size="sm">{s.userEmail}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {s.folder}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={s.permission === 'write' ? 'label' : 'gray'} c={s.permission === 'write' ? 'dark' : undefined}>
                    {s.permission}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" aria-label={`Revoke ${s.folder} from ${s.userEmail}`} onClick={() => revoke.mutate(s.id)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {shares.data && shares.data.data.length === 0 ? (
          <Text size="sm" c="dimmed" p="md">
            Nothing is shared yet.
          </Text>
        ) : null}
      </Paper>
    </Stack>
  );
}
